import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { TEST_IDS } from "@/constants/testIds";

export default function Pricing() {
    const { t } = useI18n();
    const tiers = [
        { key: "free", ...t.landing.pricingTiers.free, highlight: false, ctaTo: "/register" },
        { key: "starter", ...t.landing.pricingTiers.starter, highlight: false, ctaTo: "/register" },
        { key: "growth", ...t.landing.pricingTiers.growth, highlight: true, ctaTo: "/register" },
        { key: "enterprise", ...t.landing.pricingTiers.enterprise, highlight: false, ctaTo: "/register", enterprise: true },
    ];
    return (
        <section id="pricing" className="relative py-24 sm:py-32">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-40 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-brand-violet/10 blur-3xl" />
            </div>

            <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
                <div className="max-w-3xl">
                    <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan">Pricing</div>
                    <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl tracking-tighter text-white">
                        {t.landing.pricingTitle}
                    </h2>
                    <p className="mt-4 text-lg text-zinc-400 max-w-2xl">{t.landing.pricingSub}</p>
                </div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {tiers.map((tier, i) => (
                        <motion.div
                            key={tier.key}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            viewport={{ once: true }}
                            data-testid={TEST_IDS.landing.pricingTier(tier.key)}
                            className={`relative p-7 rounded-2xl border transition ${
                                tier.highlight
                                    ? "bg-gradient-to-b from-brand-indigo/20 to-brand-surface border-brand-indigo/40 shadow-[0_20px_60px_-20px_rgba(99,102,241,0.5)]"
                                    : "bg-brand-surface border-white/5 hover:border-white/15"
                            }`}
                        >
                            {tier.highlight && (
                                <span className="absolute -top-3 left-6 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-brand-cyan text-brand-void">
                                    {t.landing.pricingBadgePopular}
                                </span>
                            )}
                            <div className="text-sm font-semibold text-zinc-300">{tier.name}</div>
                            <div className="mt-4 flex items-baseline gap-2">
                                <span className="font-display font-black text-4xl tracking-tighter text-white">{tier.price}</span>
                                <span className="text-xs text-zinc-500">/ {tier.period}</span>
                            </div>
                            <p className="mt-3 text-sm text-zinc-400 min-h-[40px]">{tier.desc}</p>
                            <Link
                                to={tier.ctaTo}
                                data-testid={TEST_IDS.landing.pricingCta(tier.key)}
                                className={`mt-6 block w-full text-center py-2.5 rounded-full font-semibold text-sm transition ${
                                    tier.highlight
                                        ? "btn-cta text-white"
                                        : "bg-white/5 hover:bg-white/10 text-white border border-white/10"
                                }`}
                            >
                                {tier.enterprise ? t.landing.pricingCtaEnterprise : t.landing.pricingCta}
                            </Link>
                            <ul className="mt-6 space-y-2.5">
                                {tier.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                                        <Check size={16} className="text-brand-cyan mt-0.5 shrink-0" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
